import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DEFAULT_PERSON_SELECTIONS } from 'app/constants/person.constants';
import { IChartSelections, IGenerateDataFuncResult, IPieChartOptions, ISelectionsDTO } from 'app/models/chart.models';
import { IGenerateRaceDataFuncResult, Races } from 'app/models/person.models';
import { animateTopScroll } from 'app/utils/animation.utils';
import { Subscription } from 'rxjs';
import { HeaderService } from 'app/services/header.service';
import { IExportableChart } from 'app/utils/pdf.utils';
import { ByRaceChartComponent } from './by-race-chart/by-race-chart.component';
import { PieChartComponent } from 'app/components/shared/pie-chart/pie-chart.component';
import { FilterService } from 'app/services/filter.service';
import { CitationChartsService } from 'app/services/citation-charts.service';
import { CitationCharts, IChartValues } from 'app/models/citation-charts.models';
import { generateChartDataWithAllLabels, generateChartDataWithFixedDataLabels } from 'app/utils/chart.utils';
import { PageType } from 'app/models/pdf.models';
import { PdfService } from 'app/services/pdf.service';
import {
  CONTRABAND_EVIDENCE,
  REASON_FOR_SEARCH,
  REASON_FOR_STOP,
  RESULT_OF_STOP,
} from '../../shared/filter-dialog/stop-details-tab/constants';
import { NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CardComponent } from '../../shared/card/card.component';
import { PieChartComponent as PieChartComponent_1 } from '../../shared/pie-chart/pie-chart.component';

@Component({
  selector: 'app-persons',
  templateUrl: './persons.component.html',
  styleUrls: ['./persons.component.scss'],
  imports: [NgIf, MatProgressSpinner, CardComponent, ByRaceChartComponent, PieChartComponent_1],
})
export class PersonsComponent implements OnInit, OnDestroy {
  loading = false;
  selections: IChartSelections = DEFAULT_PERSON_SELECTIONS;
  chartsData: IChartValues[] = [];

  @ViewChild('raceChart')
  raceChart!: ByRaceChartComponent;

  @ViewChild('genderChart')
  genderChart!: PieChartComponent;

  @ViewChild('contrabandChart')
  contrabandChart!: PieChartComponent;

  @ViewChild('reasonStopChart')
  reasonForStopChart!: PieChartComponent;

  @ViewChild('reasonSearchChart')
  reasonForSearchChart!: PieChartComponent;

  @ViewChild('resultStopChart')
  resultOfStopChart!: PieChartComponent;

  @ViewChild('ageChart')
  ageChart!: PieChartComponent;

  readonly chartTitles = {
    RACE_ETHNICITY: 'Race/Ethnicity',
    CITATIONS_BY_GENDER: 'Citations by Gender',
    CONTRABAND_OR_EVIDENCE: 'Contraband or Evidence Discovered',
    REASON_FOR_STOP: 'Reason for Stop',
    REASON_FOR_SEARCH: 'Reason for Search',
    RESULT_OF_STOP: 'Result of the Stop',
    CITATIONS_BY_AGE_GROUP: 'Citations by Age Group',
  };
  private readonly subscriptions: Subscription = new Subscription();

  private exportSubscription!: Subscription;

  constructor(
    private headerService: HeaderService,
    private filterService: FilterService,
    private citationChartsService: CitationChartsService,
    private pdfService: PdfService,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadChartsData();

    this.exportSubscription = this.headerService.exportChange.subscribe(() => {
      this.exportToPdf();
    });

    const filtersSub = this.filterService.applyFiltersChange.subscribe(() => {
      this.clearSelections();
      this.loadChartsData();
    });
    this.subscriptions.add(filtersSub);
  }

  ngOnDestroy(): void {
    this.exportSubscription.unsubscribe();
    !!this.subscriptions && this.subscriptions.unsubscribe();
  }

  loadChartsData(): void {
    const selectionsDTO = this.combineSelectionsDTO();
    if (selectionsDTO) {
      this.citationChartsService.getPersonsCharts(selectionsDTO, this.filterService.filters).subscribe((res) => {
        this.chartsData = res;
        this.loading = false;
        this.selections = { ...this.selections };
      });
    }
  }

  ageGroupChartName = CitationCharts.DRIVER_AGE_GROUP;

  ageGroupChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Minor',
        },
      },
    },
  };

  generateAgeGroupData = (): IGenerateDataFuncResult => {
    const { total, filtered, labels } = generateChartDataWithAllLabels(this.chartsData, this.ageGroupChartName);

    return {
      total: [...total.values()],
      filtered: [...filtered.values()],
      labels,
    };
  };

  contrabandChartName = CitationCharts.CONTRABAND_OR_EVIDENCE_DISCOVERED;

  contrabandChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Contraband/Evidence',
          font: {
            size: 12,
          },
        },
      },
    },
  };

  generateContrabandData = (): IGenerateDataFuncResult => {
    const { total, filtered, labels } = generateChartDataWithAllLabels(this.chartsData, this.contrabandChartName);

    return {
      total: [...total.values()],
      filtered: [...filtered.values()],
      labels: labels.map((val) => CONTRABAND_EVIDENCE.find((item) => item.value === val)?.label || val),
    };
  };

  genderChartName = CitationCharts.GENDER;

  genderChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Gender',
        },
      },
    },
  };

  generateGenderData = (): IGenerateDataFuncResult => {
    const { total, filtered, labels } = generateChartDataWithAllLabels(this.chartsData, this.genderChartName);

    return {
      total: [...total.values()],
      filtered: [...filtered.values()],
      labels,
    };
  };

  reasonForSearchChartName = CitationCharts.REASON_FOR_SEARCH;

  reasonForSearchChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Reason',
        },
      },
    },
  };

  generateReasonForSearchData = (): IGenerateDataFuncResult => {
    const { total, filtered, labels } = generateChartDataWithAllLabels(this.chartsData, this.reasonForSearchChartName);

    return {
      total: [...total.values()],
      filtered: [...filtered.values()],
      labels: labels.map((val) => REASON_FOR_SEARCH.find((item) => item.value === val)?.label || val),
    };
  };

  reasonForStopChartName = CitationCharts.REASON_FOR_STOP;

  reasonForStopChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Reason',
        },
      },
    },
  };

  generateReasonForStopData = (): IGenerateDataFuncResult => {
    const { total, filtered, labels } = generateChartDataWithAllLabels(this.chartsData, this.reasonForStopChartName);

    return {
      total: [...total.values()],
      filtered: [...filtered.values()],
      labels: labels.map((val) => REASON_FOR_STOP.find((item) => item.value === val)?.label || val),
    };
  };

  resultOfStopChartName = CitationCharts.RESULT_OF_THE_STOP;

  resultOfStopChartOptions: IPieChartOptions = {
    plugins: {
      legend: {
        title: {
          text: 'Result',
        },
      },
    },
  };

  generateResultOfStopData = (): IGenerateDataFuncResult => {
    const { total, filtered, labels } = generateChartDataWithAllLabels(this.chartsData, this.resultOfStopChartName);

    return {
      total: [...total.values()],
      filtered: [...filtered.values()],
      labels: labels.map((val) => RESULT_OF_STOP.find((item) => item.value === val)?.label || val),
    };
  };

  raceChartName = CitationCharts.RACE_ETHNICITY;

  generateRaceData = (): IGenerateRaceDataFuncResult => {
    const targetLabels = [
      Races.BLACK,
      Races.ASIAN_PACIFIC_ISLANDER,
      Races.WHITE,
      Races.HISPANIC_LATINO,
      Races.ALASKA_NATIVE_AMERICAN_INDIAN,
    ];

    const { total, filtered } = generateChartDataWithFixedDataLabels(this.chartsData, targetLabels, this.raceChartName);

    const totalWithLabels = [...total].map(([key, value]) => ({
      label: key,
      value,
    })) as { label: Races; value: number }[];

    const totalSorted = totalWithLabels.sort((a, b) => b.value - a.value);
    const labels = totalSorted.map((item) => item.label);

    const filteredWithLabels = totalSorted.map(({ label }) => ({
      label,
      value: filtered.get(label) ?? 0,
    }));
    return { total: totalSorted, filtered: filteredWithLabels, labels };
  };

  clearSelections(): void {
    this.selections = DEFAULT_PERSON_SELECTIONS;
  }

  setSelections(selections: string[], resetOther: boolean, name: string) {
    const newSelections = resetOther ? { ...DEFAULT_PERSON_SELECTIONS } : { ...this.selections };
    this.selections = { ...newSelections, [name]: selections };
    this.loadChartsData();
  }

  combineSelectionsDTO(): ISelectionsDTO {
    const result = [];
    for (const chartName of Object.keys(DEFAULT_PERSON_SELECTIONS)) {
      if (this.selections?.[chartName]?.length > 0) {
        result.push({
          chart: chartName,
          values: this.selections?.[chartName],
        });
      }
    }
    return result;
  }

  fullScreenChart: string | null = null;

  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;
  private scrollPosition = 0;

  toggleFullScreen(chart: string) {
    if (chart == this.fullScreenChart) {
      this.fullScreenChart = null;
      animateTopScroll(this.scrollPosition, this.scrollContainer.nativeElement);
    } else {
      this.fullScreenChart = chart;
      this.scrollPosition = this.scrollContainer.nativeElement.scrollTop;
    }
  }

  exportToPdf() {
    let charts: IExportableChart[] = [
      {
        chart: this.raceChart.exportToImage(),
        name: this.chartTitles.RACE_ETHNICITY,
      },
      {
        chart: this.genderChart.exportToImage(),
        name: this.chartTitles.CITATIONS_BY_GENDER,
      },
      {
        chart: this.contrabandChart.exportToImage(),
        name: this.chartTitles.CONTRABAND_OR_EVIDENCE,
      },
      {
        chart: this.reasonForStopChart.exportToImage(),
        name: this.chartTitles.REASON_FOR_STOP,
      },
      {
        chart: this.reasonForSearchChart.exportToImage(),
        name: this.chartTitles.REASON_FOR_SEARCH,
      },
      {
        chart: this.resultOfStopChart.exportToImage(),
        name: this.chartTitles.RESULT_OF_STOP,
      },
      {
        chart: this.ageChart.exportToImage(),
        name: this.chartTitles.CITATIONS_BY_AGE_GROUP,
      },
    ];
    if (this.fullScreenChart) {
      charts = [charts[Number.parseInt(this.fullScreenChart)]];
    }
    this.pdfService.generateChartReportFile(
      PageType.PERSONS,
      charts,
      this.filterService.filters,
      'tda_persons',
      'Report - Persons',
    );
  }
}
